import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Plus, ArrowUp, ShieldCheck, Loader2 } from 'lucide-react';
import assistantService from '../../services/assistantService';
import { PageHeader, Button, IconButton } from '../../components/ui';
import '../../styles/theme.css';
import '../../styles/assistant-panel.css';

interface Msg { role: 'user' | 'assistant'; content: string; }

const SUGGESTIONS = [
  'What documents do I have and their status?',
  'What is the maturity date in the treasury loan?',
  'Which documents are waiting for my review?',
];

const Assistant: React.FC = () => {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [confidential, setConfidential] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, busy]);

  const reset = () => { setMessages([]); setSessionId(null); setConfidential(false); };

  const send = async (text: string) => {
    const t = text.trim();
    if (!t || busy) return;
    setMessages((m) => [...m, { role: 'user', content: t }]);
    setInput('');
    setBusy(true);
    try {
      const res = await assistantService.sendMessage(t, sessionId);
      setSessionId(res.session_id);
      setConfidential(res.confidential_touched);
      setMessages((m) => [...m, { role: 'assistant', content: res.reply }]);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: 'Sorry — I couldn’t reach the assistant just now.' }]);
    } finally { setBusy(false); }
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); }
  };

  return (
    <div>
      <PageHeader title="Assistant"
        subtitle="Ask about your documents and the pipeline, or halt a running document. Runs on your in-infra model, so it can read confidential documents safely."
        actions={<Button icon={Plus} variant="secondary" size="sm" onClick={reset}>New conversation</Button>} />

      <div className="mx-card" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 210px)', minHeight: 380 }}>
        <div className="as-body" ref={bodyRef} style={{ background: 'var(--mx-surface)', borderRadius: 'var(--mx-radius) var(--mx-radius) 0 0' }}>
          {messages.length === 0 && !busy ? (
            <div className="as-empty">
              <Sparkles size={26} style={{ color: 'var(--mx-accent)', marginBottom: 8 }} />
              <div style={{ marginBottom: 14 }}>Ask about your documents or the pipeline.</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {SUGGESTIONS.map((s) => (
                  <button key={s} className="mx-btn mx-btn-outline mx-btn-sm" style={{ justifyContent: 'flex-start' }}
                    onClick={() => send(s)}>{s}</button>
                ))}
              </div>
            </div>
          ) : messages.map((m, i) => (
            <div key={i} className={`as-msg ${m.role}`}>{m.content}</div>
          ))}
          {busy && <div className="as-msg assistant pending"><Loader2 size={13} className="mx-spin" /> thinking…</div>}
        </div>

        {confidential && (
          <div className="as-note" style={{ borderTop: '1px solid var(--mx-border)' }}>
            <ShieldCheck size={13} style={{ color: 'var(--mx-success)' }} />
            This conversation touched a confidential document — running on your local model only.
          </div>
        )}

        <div className="as-foot">
          <textarea className="as-input" value={input} placeholder="Ask the assistant…"
            onChange={(e) => setInput(e.target.value)} onKeyDown={onKey} rows={1} />
          <IconButton icon={ArrowUp} label="Send" variant="primary" onClick={() => send(input)}
            disabled={busy || !input.trim()} />
        </div>
      </div>
    </div>
  );
};

export default Assistant;
